import { ClusterRedirect } from 'generated/graphql';
import { env } from './env';

type Config = {
  deployEnvironment: string;
  managementOrigin: string;
  managementAppZeroOrigin: string;
  apiGateway: string;
  apiAppZeroGateway: string;
  asrPlexOrigin: string;
  asrPlexAppZeroOrigin: string;
  nvoqWs: string;
  nvoqWsMaximumConnections: number;
  segmentAppId: string;
  splitioApiKey: string;
};

type EnvMapping = {
  local: Config;
  development: Config;
  staging: Config;
  production: Config;
  preview: (arg1: string) => Config; // Special case to allow namespaced previews,
  sironaPreview: (arg1: string) => Config; // Special case to allow namespaced previews
};

const OKTETO_NAMESPACE = env.OKTETO_NAMESPACE || '';
export const SIRONA_PREVIEW: string = env.SIRONA_PREVIEW || '';
const envMapping: EnvMapping = {
  local: {
    deployEnvironment: 'local',
    managementOrigin: 'http://localhost:3042',
    managementAppZeroOrigin: 'http://localhost:3042',
    apiGateway: 'http://localhost:4000',
    // fake a second cluster by attempting to contact a graphql server on a second port
    apiAppZeroGateway: 'http://localhost:5000',
    asrPlexOrigin: 'ws://localhost:4000',
    asrPlexAppZeroOrigin: 'ws://localhost:5000',
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey:
      env.NODE_ENV === 'test' || env.LOCAL_SPLITS === 'true'
        ? 'localhost'
        : 'ulc2iijia2jp42b2mf1l600k61c42iufnmav',
  },
  preview: (oktetoNamespace: string) => ({
    deployEnvironment: 'preview',
    managementOrigin: `https://admin-${oktetoNamespace}.preview.dev.sironamedical.com`,
    managementAppZeroOrigin: `https://admin-${oktetoNamespace}.preview.dev.sironamedical.com`,
    apiGateway: `https://graphql-${oktetoNamespace}.preview.dev.sironamedical.com`,
    apiAppZeroGateway: `https://graphql-${oktetoNamespace}.preview.dev.sironamedical.com`,
    asrPlexOrigin: 'wss://asr-plex.dev.sironamedical.com',
    asrPlexAppZeroOrigin: 'wss://asr-plex.dev.sironamedical.com',
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey: 'ulc2iijia2jp42b2mf1l600k61c42iufnmav',
  }),
  sironaPreview: (sironaPreview: string) => ({
    deployEnvironment: 'preview',
    managementOrigin: `https://admin-${sironaPreview}.preview.dev.sironamedical.com`,
    managementAppZeroOrigin: `https://admin-${sironaPreview}.preview.dev.sironamedical.com`,
    apiGateway: `https://graphql-${sironaPreview}.dev.sironamedical.com`,
    apiAppZeroGateway: `https://graphql-${sironaPreview}.dev.sironamedical.com`,
    asrPlexOrigin: 'wss://asr-plex.dev.sironamedical.com',
    asrPlexAppZeroOrigin: 'wss://asr-plex.dev.sironamedical.com',
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey: 'ulc2iijia2jp42b2mf1l600k61c42iufnmav',
  }),
  development: {
    deployEnvironment: 'development',
    // Fallbacks for when the app is served from a non-app.* host in this deploy env (e.g. a
    // Docker/e2e build tested via localhost) — real app.* hosts derive these dynamically instead.
    managementOrigin: 'https://app.dev.sironamedical.com',
    managementAppZeroOrigin: 'https://app.us.dev.sironamedical.com',
    apiGateway: 'https://graphql.dev.sironamedical.com',
    apiAppZeroGateway: 'https://graphql.us.dev.sironamedical.com',
    asrPlexOrigin: 'wss://asr-plex.dev.sironamedical.com',
    asrPlexAppZeroOrigin: 'wss://asr-plex.us.dev.sironamedical.com',
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey: 'ulc2iijia2jp42b2mf1l600k61c42iufnmav',
  },
  staging: {
    deployEnvironment: 'staging',
    managementOrigin: 'https://app.stg.sironamedical.com',
    managementAppZeroOrigin: 'https://app.us.stg.sironamedical.com',
    apiGateway: 'https://graphql.stg.sironamedical.com',
    asrPlexOrigin: 'wss://asr-plex.stg.sironamedical.com',
    apiAppZeroGateway: 'https://graphql.us.stg.sironamedical.com',
    asrPlexAppZeroOrigin: 'wss://asr-plex.us.stg.sironamedical.com',
    nvoqWs: 'wss://healthcare.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 7,
    segmentAppId: 'fHPBx5PtUtAR3Si5Fr6m8BurpFGS8Grv',
    splitioApiKey: '69fu9r39ra5d4914kd7g27p246i5gsvhn3ko',
  },
  production: {
    deployEnvironment: 'production',
    managementOrigin: 'https://app.sironamedical.com',
    managementAppZeroOrigin: 'https://app.us.sironamedical.com',
    apiGateway: 'https://graphql.prod.sironamedical.com',
    asrPlexOrigin: 'wss://asr-plex.prod.sironamedical.com',
    apiAppZeroGateway: 'https://graphql.us.prod.sironamedical.com',
    asrPlexAppZeroOrigin: 'wss://asr-plex.us.prod.sironamedical.com',
    nvoqWs: 'wss://healthcare.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 7,
    segmentAppId: 'PAwhfXbi6zGSCvgr5I8hC9kF3ZphVj0g',
    splitioApiKey: 'behco42hidesjj8f2n86lco36qvui88fvi7s',
  },
};

const getConfigFromDeployEnv = () => {
  switch (env.DEPLOY_ENV) {
    case 'production':
      return envMapping.production;
    case 'staging':
      return envMapping.staging;
    case 'development':
      return envMapping.development;
    case 'preview':
      return SIRONA_PREVIEW
        ? envMapping.sironaPreview(SIRONA_PREVIEW)
        : envMapping.preview(OKTETO_NAMESPACE);
    default:
      return envMapping.local;
  }
};

const config: Config = getConfigFromDeployEnv();

// If a environment variable is provided, we try to parse and use it
const envOrFallback = <T>(env: Nullable<string | T>, fallback: T): T => {
  if (env != null) {
    if (typeof env === 'string') {
      try {
        return JSON.parse(env);
      } catch (e: any) {
        return env as T;
      }
    } else {
      return env;
    }
  }
  return fallback;
};

const API_GATEWAY_URL: string = envOrFallback(env.API_GATEWAY_URL, config.apiGateway);
const API_APP_ZERO_GATEWAY_URL: string = envOrFallback(
  env.API_APP_ZERO_GATEWAY_URL,
  config.apiAppZeroGateway
);

const GRAPHQL_URL: string = envOrFallback(env.GRAPHQL_ENDPOINT, `${API_GATEWAY_URL}/graphql`);
const GRAPHQL_APP_ZERO_URL: string = envOrFallback(
  env.GRAPHQL_APP_ZERO_ENDPOINT,
  `${API_APP_ZERO_GATEWAY_URL}/graphql`
);

const managementOrigin = config.managementOrigin;
const ADMIN_URL: string = envOrFallback(env.ADMIN_URL, `${managementOrigin}/manage/users`);
const EXAM_MANAGER_URL: string = envOrFallback(
  env.EXAM_MANAGER_URL,
  `${managementOrigin}/manage/exam`
);
const ORDER_MANAGER_URL: string = envOrFallback(
  env.ORDER_MANAGER_URL,
  `${managementOrigin}/manage/order`
);
const PATIENT_MANAGER_URL: string = envOrFallback(
  env.PATIENT_MANAGER_URL,
  `${managementOrigin}/manage/patient`
);

const managementAppZeroOrigin = config.managementAppZeroOrigin;
const ADMIN_APP_ZERO_URL: string = envOrFallback(
  env.ADMIN_APP_ZERO_URL,
  `${managementAppZeroOrigin}/manage/users`
);
const EXAM_MANAGER_APP_ZERO_URL: string = envOrFallback(
  env.EXAM_MANAGER_APP_ZERO_URL,
  `${managementAppZeroOrigin}/manage/exam`
);
const ORDER_MANAGER_APP_ZERO_URL: string = envOrFallback(
  env.ORDER_MANAGER_APP_ZERO_URL,
  `${managementAppZeroOrigin}/manage/order`
);
const PATIENT_MANAGER_APP_ZERO_URL: string = envOrFallback(
  env.PATIENT_MANAGER_APP_ZERO_URL,
  `${managementAppZeroOrigin}/manage/patient`
);

const GRAPHQL_WS_URL: string = envOrFallback(
  env.GRAPHQL_WS_ENDPOINT,
  `${API_GATEWAY_URL.replace(/^http/, 'ws')}/graphql`
);

const GRAPHQL_WS_APP_ZERO_URL: string = envOrFallback(
  env.GRAPHQL_WS_APP_ZERO_ENDPOINT,
  `${API_APP_ZERO_GATEWAY_URL.replace(/^http/, 'ws')}/graphql`
);

const asrPlexOrigin: string = config.asrPlexOrigin;
const asrPlexAppZeroOrigin: string = config.asrPlexAppZeroOrigin;
const ASR_PLEX_WS_URL: string = envOrFallback(
  env.ASR_PLEX_WS_URL,
  `${asrPlexOrigin}/ws/transcribe`
);
const ASR_PLEX_APP_ZERO_WS_URL: string = envOrFallback(
  env.ASR_PLEX_APP_ZERO_WS_URL,
  `${asrPlexAppZeroOrigin}/ws/transcribe`
);
const ASR_PLEX_VAD_WS_URL: string = envOrFallback(
  env.ASR_PLEX_VAD_WS_URL,
  `${asrPlexOrigin}/ws/vad`
);
const ASR_PLEX_VAD_APP_ZERO_WS_URL: string = envOrFallback(
  env.ASR_PLEX_VAD_APP_ZERO_WS_URL,
  `${asrPlexAppZeroOrigin}/ws/vad`
);

export const DEPLOY_ENV: string = config.deployEnvironment;
export const NVOQ_WS_URL: string = envOrFallback(env.NVOQ_WS_URL, config.nvoqWs);
export const NVOQ_WS_MAXIMUM_CONNECTIONS: number = envOrFallback(
  env.NVOQ_WS_MAXIMUM_CONNECTIONS,
  config.nvoqWsMaximumConnections
);
export const SEGMENT_APP_ID = config.segmentAppId;
export const SPLITIO_API_KEY = config.splitioApiKey;
export const REPORTER_DEBUG: string = envOrFallback(env.REPORTER_DEBUG, 'false');
export const DATADOG_APP_VERSION: string = env.GIT_TAG ?? '?.?.?';
export const LOG_LEVEL: string = envOrFallback(env.LOG_LEVEL, 'info');

export type ClusterConnections = {
  apiGatewayUrl: string;
  graphqlUrl: string;
  graphqlWs: string;
  asrPlexWs: string;
  asrPlexVadWs: string;
};

export type AdminClusterUrls = {
  adminUrl: string;
  examManagerUrl: string;
  orderManagerUrl: string;
  patientManagerUrl: string;
};

export const ORIGINAL_CLUSTER_CONNECTIONS = {
  apiGatewayUrl: API_GATEWAY_URL,
  graphqlUrl: GRAPHQL_URL,
  graphqlWs: GRAPHQL_WS_URL,

  asrPlexWs: ASR_PLEX_WS_URL,
  asrPlexVadWs: ASR_PLEX_VAD_WS_URL,
};

export const APP_ZERO_CLUSTER_CONNECTIONS = {
  apiGatewayUrl: API_APP_ZERO_GATEWAY_URL,
  graphqlUrl: GRAPHQL_APP_ZERO_URL,
  graphqlWs: GRAPHQL_WS_APP_ZERO_URL,

  asrPlexWs: ASR_PLEX_APP_ZERO_WS_URL,
  asrPlexVadWs: ASR_PLEX_VAD_APP_ZERO_WS_URL,
};

const MATCH_APP_ZERO = /^app\.us\./;

/**
 * Calculates the default urls for the cluster based on the hostname.
 * Prefer using `DEFAULT_CLUSTER_CONNECTIONS`. This function is exported for testing.
 */
export function defaultClusterConnections(): ClusterConnections {
  const isAppZeroFrontend = MATCH_APP_ZERO.test(location.hostname);
  return isAppZeroFrontend ? APP_ZERO_CLUSTER_CONNECTIONS : ORIGINAL_CLUSTER_CONNECTIONS;
}

export let DEFAULT_CLUSTER_CONNECTIONS = defaultClusterConnections();
export function __test__resetDefaultClusterConnections() {
  DEFAULT_CLUSTER_CONNECTIONS = defaultClusterConnections();
}

const ORIGINAL_CLUSTER_ADMIN_URLS = {
  adminUrl: ADMIN_URL,
  examManagerUrl: EXAM_MANAGER_URL,
  orderManagerUrl: ORDER_MANAGER_URL,
  patientManagerUrl: PATIENT_MANAGER_URL,
};

const APP_ZERO_CLUSTER_ADMIN_URLS = {
  adminUrl: ADMIN_APP_ZERO_URL,
  examManagerUrl: EXAM_MANAGER_APP_ZERO_URL,
  orderManagerUrl: ORDER_MANAGER_APP_ZERO_URL,
  patientManagerUrl: PATIENT_MANAGER_APP_ZERO_URL,
};

/**
 * Calculates the default admin urls for the cluster based on the hostname.
 * Prefer using `DEFAULT_CLUSTER_ADMIN_URLS`. This function is exported for testing.
 */
export function defaultAdminClusterUrls(): AdminClusterUrls {
  const isAppZeroFrontend = MATCH_APP_ZERO.test(location.hostname);
  return isAppZeroFrontend ? APP_ZERO_CLUSTER_ADMIN_URLS : ORIGINAL_CLUSTER_ADMIN_URLS;
}

/**
 * Currently, admin urls use hostname routing only.
 * They are explicitly NOT a part of the CLUSTER_REDIRECT flag.
 */
export const DEFAULT_CLUSTER_ADMIN_URLS = defaultAdminClusterUrls();

/**
 * returns a set of urls dependent on CLUSTER_REDIRECT flag and current hostname
 */
export function getConnectionUrls(clusterRedirect: ClusterRedirect): ClusterConnections {
  switch (clusterRedirect) {
    case ClusterRedirect.AppZero:
      return APP_ZERO_CLUSTER_CONNECTIONS;

    case ClusterRedirect.Original:
      return ORIGINAL_CLUSTER_CONNECTIONS;

    default:
      return DEFAULT_CLUSTER_CONNECTIONS;
  }
}
